import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, InputAdornment, Popover, TextField, Typography } from '@mui/material';
import { Search } from '@mui/icons-material';
import type { SpecCatalogEntry } from '../../services/integrationApi';
import { tokens } from '../../theme/tokens';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { groupSpecCatalog, searchSpecCatalog, SpecSearchHit } from '../../utils/specCatalogSearch';
import { isNoisySpecLabel, specFit } from './mappingModel';

/**
 * The one spec picker behind "Filled from Motzu spec" on the Spec metafields editors (legacy section and
 * Mapping v2). Searches the catalog by keyword across label, canonical key, category and aliases
 * (`utils/specCatalogSearch`, MOT-361) and lists the hits grouped by spec category with the count of
 * synced products carrying each spec. The catalog itself is read once per store by the parent.
 */
export interface SpecPickerPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  catalog: SpecCatalogEntry[];
  /** False for an older API response with labels only; noise is then not hidden by count. */
  hasCounts: boolean;
  /** The definition being filled; drives the fit hint on each option. */
  definitionType: string | null;
  /** The spec currently filling the definition, highlighted in the list. */
  currentLabel?: string;
  onPick: (label: string) => void;
  /** Synced products the counts are out of, when the API said. */
  syncedProducts?: number | null;
}

/** Options shown with no query (the most used) and with one. */
const MOST_USED_LIMIT = 10;
const SEARCH_LIMIT = 40;

const groupHeaderSx = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: 1,
  px: 1,
  py: 0.5,
  bgcolor: tokens.surface.sunken,
  borderRadius: `${tokens.radius.small}px`,
} as const;

const capsSx = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: tokens.ink.muted,
} as const;

export function pickerHits(catalog: SpecCatalogEntry[], query: string, hasCounts: boolean): Array<SpecSearchHit<SpecCatalogEntry>> {
  const q = query.trim();
  const ranked = searchSpecCatalog(catalog, q);
  if (!q) {
    return ranked.filter((hit) => !isNoisySpecLabel(hit.entry.label, hit.entry.productCount, hasCounts)).slice(0, MOST_USED_LIMIT);
  }
  return ranked.slice(0, SEARCH_LIMIT);
}

export const SpecPickerPopover: React.FC<SpecPickerPopoverProps> = ({
  open,
  anchorEl,
  onClose,
  catalog,
  hasCounts,
  definitionType,
  currentLabel,
  onPick,
  syncedProducts,
}) => {
  const { t } = useAppTranslation();
  const [query, setQuery] = useState('');

  // A fresh search for every definition the picker opens on.
  useEffect(() => {
    if (open) setQuery('');
  }, [open, anchorEl]);

  const hits = useMemo(() => pickerHits(catalog, query, hasCounts), [catalog, query, hasCounts]);
  const groups = useMemo(() => groupSpecCatalog(hits), [hits]);
  const trimmed = query.trim();

  const countText = (entry: SpecCatalogEntry): string => {
    if (entry.productCount <= 0) return t('integrations.specPicker.inCatalog');
    if (syncedProducts && syncedProducts >= entry.productCount) {
      return t('integrations.specPicker.productsOf', { count: entry.productCount, total: syncedProducts });
    }
    return t('integrations.specPicker.products', { count: entry.productCount });
  };

  const whyText = (hit: SpecSearchHit<SpecCatalogEntry>): string | null => {
    if (!trimmed) return null;
    if (hit.matchedOn === 'alias' && hit.alias) return t('integrations.specPicker.alsoCalled', { alias: hit.alias });
    if (hit.matchedOn === 'key' && hit.entry.specPath) return t('integrations.specPicker.matchedKey', { key: hit.entry.specPath });
    if (hit.matchedOn === 'category' && hit.entry.category) {
      return t('integrations.specPicker.matchedCategory', { category: hit.entry.category });
    }
    return null;
  };

  return (
    <Popover
      open={open && Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      slotProps={{ paper: { sx: { width: 'min(440px, 90vw)', borderRadius: `${tokens.radius.panel - 2}px`, boxShadow: tokens.shadow.flyout, p: 1.5 } } }}
    >
      <TextField
        autoFocus
        size="small"
        fullWidth
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={t('integrations.specPicker.placeholder')}
        inputProps={{ 'aria-label': t('integrations.specPicker.search') }}
        slotProps={{ input: { startAdornment: (<InputAdornment position="start"><Search sx={{ fontSize: 16 }} /></InputAdornment>) } }}
      />
      <Typography sx={{ mt: 1.25, mb: 0.75, ...capsSx }}>
        {trimmed ? t('integrations.specPicker.matches', { count: hits.length }) : t('integrations.specPicker.mostUsed')}
      </Typography>
      <Box role="listbox" aria-label={t('integrations.specPicker.results')} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: 340, overflow: 'auto' }}>
        {groups.map((group) => (
          <Box key={group.category} role="group" aria-label={group.category} sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Box sx={groupHeaderSx}>
              <Typography sx={{ ...capsSx, color: tokens.ink.body }}>{group.category}</Typography>
              <Typography sx={{ fontSize: 11, color: tokens.ink.faint, fontVariantNumeric: 'tabular-nums' }}>
                {t('integrations.specPicker.specsInGroup', { count: group.entries.length })}
              </Typography>
            </Box>
            {group.entries.map((hit) => {
              const { entry } = hit;
              const fit = definitionType ? specFit(entry, definitionType) : { text: 'Fits', ok: true };
              const current = Boolean(currentLabel) && currentLabel === entry.label;
              const why = whyText(hit);
              return (
                <Button
                  key={entry.label}
                  role="option"
                  aria-selected={current}
                  onClick={() => onPick(entry.label)}
                  sx={{
                    display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: '2px 10px', textAlign: 'left', p: 1,
                    borderRadius: `${tokens.radius.small}px`, color: tokens.ink.primary, bgcolor: current ? tokens.accent.tint : 'transparent',
                  }}
                >
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{entry.label}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: tokens.ink.muted, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {countText(entry)}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: tokens.ink.muted }} noWrap>
                    {why || (entry.sample ? t('integrations.specPicker.sample', { value: entry.sample }) : '')}
                  </Typography>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, textAlign: 'right', color: fit.ok ? tokens.signal.positive.ink : tokens.signal.caution.ink }}>
                    {fit.text}
                  </Typography>
                </Button>
              );
            })}
          </Box>
        ))}
        {hits.length === 0 && (
          <Typography sx={{ p: 1, fontSize: 12.5, color: tokens.ink.muted }}>
            {t('integrations.specPicker.noMatch', { query: trimmed })}
          </Typography>
        )}
      </Box>
      <Typography sx={{ mt: 1, fontSize: 11.5, color: tokens.ink.faint }}>{t('integrations.specPicker.hiddenNote')}</Typography>
    </Popover>
  );
};

export default SpecPickerPopover;
